/**
 * Language switcher — Task 33.2
 * Updates URL locale prefix and saves preference.
 */
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUIStore } from '@/stores/uiStore'
import { SUPPORTED_LOCALES } from '@/lib/i18n'
import { Globe } from 'lucide-react'

const LOCALE_LABELS: Record<string, string> = {
  'en-US': 'English',
  'fr-FR': 'Français',
  'ar-SA': 'العربية',
  'es-ES': 'Español',
}

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const { setLocale } = useUIStore()
  const navigate = useNavigate()
  const location = useLocation()

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const locale = e.target.value
    i18n.changeLanguage(locale)
    setLocale(locale)

    // Update URL locale prefix
    const segments = location.pathname.split('/')
    if (SUPPORTED_LOCALES.includes(segments[1])) {
      segments[1] = locale
    } else {
      segments.splice(1, 0, locale)
    }
    navigate(segments.join('/') || '/')
  }

  return (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-gray-400" aria-hidden="true" />
      <select
        value={i18n.language}
        onChange={handleChange}
        className="border-0 bg-transparent text-sm text-gray-600 focus:outline-none focus:ring-0"
        aria-label="Select language"
      >
        {SUPPORTED_LOCALES.map((locale: string) => (
          <option key={locale} value={locale}>
            {LOCALE_LABELS[locale] ?? locale}
          </option>
        ))}
      </select>
    </div>
  )
}
